import "./trust-pages.css";
import { PRODUCT, TERMS_ROUTE, WEB_ROUTES } from "./config";

export const SUPPORT_EMAIL = "support@convoful.com";
export const TRUST_EFFECTIVE_DATE = "10 August 2026";
export const TERMS_EFFECTIVE_DATE = "10 August 2026";
export const ACCOUNT_DELETION_RELEASE_ACCEPTED = true;
export const APPLE_STANDARD_EULA_URL =
  "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";

export const STRIPE_PRIVACY_POLICY = Object.freeze({
  processorPrivacyURL: "https://stripe.com/privacy",
  purchaseHistory:
    "Convoful receives the account-linked Stripe Customer, subscription, invoice, refund, and dispute state needed to provide and manage paid access.",
  paymentInfoBoundary:
    "Payment details are entered on Stripe-hosted Checkout; Convoful does not receive card or bank-account details.",
  retention:
    "Convoful keeps account-linked Stripe billing state while needed for paid access, billing resolution, legal obligations, or account deletion; Stripe separately retains payment and transaction records under its own obligations and controls.",
});

export const ACCOUNT_DELETION_POLICY = Object.freeze({
  maximumCompletionDays: 30,
  candidateTimeframe:
    "within 30 calendar days of the in-app request, or sooner where applicable law requires",
  localRemoval:
    "Offline cards, History, saved Share extension items, and local photos remain on a device until the person deletes the app from that device.",
  candidateCompletionChannel:
    "Convoful sends completion confirmation to the verified account email after full-account deletion is complete.",
});

export const PRIVACY_FACTS = Object.freeze([
  "Convoful may handle account and sign-in information, content you choose to save or share, basic technical information, and messages you send to support.",
  "Convoful uses that information to operate, secure, troubleshoot, and improve the service.",
  "Convoful does not sell personal data, show behavioral advertising, or use cross-app tracking.",
  "Information may be shared with people you choose and with service providers needed to operate Convoful.",
  "Convoful is not end-to-end encrypted or zero knowledge; authorized service operators can access server records when needed to operate, recover, support, or delete the service.",
  "Convoful does not promise a fixed deletion deadline. Deletion may take time, and device or provider backups and records kept for security or legal reasons may remain.",
]);

export const SUPPORT_FACTS = Object.freeze([
  "Never send a password, magic-link token, bearer credential, cookie, private key, or full private card text in a support request.",
  "Do not delete or reinstall the app while offline work or a saved Share extension item may still be waiting.",
  "Include the version and build shown in About—current iOS builds use the legacy Agenda! name—plus your iOS version, what you expected, and what happened instead.",
]);

function setMetaDescription(content: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  meta.content = content;
}

function setCanonical(href: string): void {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = href;
}

function header(active: "privacy" | "support" | "terms"): string {
  const termsWrap = active === "terms" ? ' style="flex-wrap: wrap"' : "";
  return `
    <header class="trust-site-header"${termsWrap}>
      <a class="trust-brand" href="${WEB_ROUTES.marketing}" aria-label="${PRODUCT.name} home">
        <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
        <span>${PRODUCT.name}</span>
      </a>
      <nav class="trust-nav" aria-label="Trust and support"${termsWrap}>
        <a href="${WEB_ROUTES.privacy}"${active === "privacy" ? ' aria-current="page"' : ""}>Privacy</a>
        <a href="${WEB_ROUTES.support}"${active === "support" ? ' aria-current="page"' : ""}>Support</a>
        <a href="${TERMS_ROUTE}"${active === "terms" ? ' aria-current="page"' : ""}>Terms</a>
        <a href="${WEB_ROUTES.howBuilt}">How it works</a>
      </nav>
    </header>`;
}

function footer(): string {
  return `
    <footer class="trust-footer">
      <div>
        <strong>${PRODUCT.name}</strong>
        <p>Conversation Cards for together. Library for later.</p>
      </div>
      <div class="trust-footer-links">
        <a href="${WEB_ROUTES.privacy}">Privacy</a>
        <a href="${WEB_ROUTES.support}">Support</a>
        <a href="${TERMS_ROUTE}">Terms</a>
        <a href="${WEB_ROUTES.appStoreRelease}">Release status</a>
      </div>
    </footer>`;
}

function preparePage(
  surface: "privacy" | "support",
  title: string,
  description: string,
  canonicalPath: string,
): void {
  document.documentElement.dataset.surface = surface;
  document.title = `${title} · ${PRODUCT.name}`;
  setMetaDescription(description);
  setCanonical(`${WEB_ROUTES.canonicalOrigin}${canonicalPath}`);
  const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
  if (skipLink) skipLink.href = "#trust-main";
}

export function renderPrivacy(root: HTMLElement): void {
  preparePage(
    "privacy",
    "Privacy",
    "A simple explanation of the information Convoful may handle and the choices available to you.",
    WEB_ROUTES.privacy,
  );

  root.innerHTML = `
    <div class="trust-shell">
      ${header("privacy")}
      <main id="trust-main" tabindex="-1">
        <section class="trust-hero trust-hero-privacy" aria-labelledby="trust-title">
          <p class="eyebrow">Privacy policy · effective ${TRUST_EFFECTIVE_DATE}</p>
          <h1 id="trust-title">Simple privacy for a nascent app.</h1>
          <p class="trust-lede">
            Convoful is new and pre-release. This page gives a basic explanation
            of the information the app may handle without promising a finished
            feature list, deletion schedule, or permanent technical design.
          </p>
          <aside class="trust-release-note" aria-label="Early product notice">
            <strong>Early software, plainly stated.</strong>
            <p>What Convoful collects and how it works may change as the product develops. The effective date above shows when this explanation was last updated.</p>
          </aside>
        </section>

        <section class="trust-fact-grid" aria-labelledby="privacy-summary">
          <div class="trust-section-heading">
            <p class="eyebrow">At a glance</p>
            <h2 id="privacy-summary">The short version.</h2>
          </div>
          <article>
            <span aria-hidden="true">01</span>
            <h3>Only what helps run the app</h3>
            <p>Account details, saved or shared content, basic technical information, and support messages.</p>
          </article>
          <article>
            <span aria-hidden="true">02</span>
            <h3>No advertising profile</h3>
            <p>Convoful does not sell personal data, show behavioral advertising, or use cross-app tracking.</p>
          </article>
          <article>
            <span aria-hidden="true">03</span>
            <h3>Not zero knowledge</h3>
            <p>The service and authorized operators may be able to read information stored on Convoful's servers.</p>
          </article>
        </section>

        <div class="trust-policy-layout">
          <nav class="trust-policy-nav" aria-label="Privacy policy sections">
            <strong>On this page</strong>
            <a href="#collect">What we may handle</a>
            <a href="#use-share">How it is used</a>
            <a href="#security">Security</a>
            <a href="#retention">Deletion</a>
            <a href="#contact">Contact</a>
          </nav>

          <div class="trust-policy-copy">
            <section id="collect" class="trust-copy-section">
              <p class="eyebrow">01 · What we may handle</p>
              <h2>A small set of useful information.</h2>
              <ul>
                <li><strong>Account and sign-in information</strong>, such as your email address and information needed to keep you signed in.</li>
                <li><strong>Content you choose to save or share</strong> through Convoful.</li>
                <li><strong>Basic technical information</strong> used to run, secure, and troubleshoot the service.</li>
                <li><strong>Messages you send to support</strong>, including anything you deliberately attach.</li>
              </ul>
              <p>Some information may stay on your device, and some may be stored by the service. The exact mix may change as this early product develops.</p>
            </section>

            <section id="use-share" class="trust-copy-section">
              <p class="eyebrow">02 · How it is used and shared</p>
              <h2>To operate Convoful—not to build an ad profile.</h2>
              <p>Convoful may use information to provide the service, keep it secure, troubleshoot problems, answer support requests, and improve the product.</p>
              <p>Information may be shared with people you choose and with service providers needed to operate Convoful, such as hosting, email, app-distribution, and payment providers when those services are used.</p>
              <p>Convoful does not sell personal data, show behavioral advertising, or use cross-app tracking.</p>
            </section>

            <section id="security" class="trust-copy-section">
              <p class="eyebrow">03 · Security</p>
              <h2>Reasonable safeguards, not impossible promises.</h2>
              <p>Convoful uses ordinary security measures intended to protect information. No service can promise perfect security.</p>
              <p><strong>Convoful is not end-to-end encrypted or zero knowledge.</strong> The service and authorized operators may be able to read server information when needed to operate, secure, recover, or support the product.</p>
            </section>

            <section id="retention" class="trust-copy-section">
              <p class="eyebrow">04 · Retention and deletion</p>
              <h2>Deletion is available, but the timetable is not promised.</h2>
              <p>You may remove information through controls that are available in the product or ask to delete your account.</p>
              <p>Convoful does not promise a fixed deletion deadline. A request may take time to review and complete. Device copies, backups, or records another provider must keep may remain after active Convoful data is removed.</p>
              <p>Email <a href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20privacy">${SUPPORT_EMAIL}</a> if you have a deletion or privacy question. Never send a password or sign-in link.</p>
            </section>

            <section id="contact" class="trust-copy-section trust-contact-card">
              <p class="eyebrow">05 · Contact and changes</p>
              <h2>Ask a privacy question.</h2>
              <p>Email <a href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20privacy">${SUPPORT_EMAIL}</a>.</p>
              <p>If this explanation changes, the effective date at the top of the page will change too.</p>
              <a class="trust-primary-action" href="${WEB_ROUTES.support}">Open support <span aria-hidden="true">→</span></a>
            </section>
          </div>
        </div>
        ${footer()}
      </main>
    </div>`;

  requestAnimationFrame(() => document.querySelector<HTMLElement>("#trust-main")?.focus());
}

export function renderSupport(root: HTMLElement): void {
  preparePage(
    "support",
    "Support",
    "Get help with Convoful sign-in, private cards, Share extension recovery, Library access, offline work, and account deletion.",
    WEB_ROUTES.support,
  );

  const accountDeletionSupportCopy = `
      <p>Use the account-deletion control if it is available in your version, or email support for help. Deletion may take time and does not have a promised completion date.</p>
      <p>Copies on a device, in backups, or held by another provider may remain after active Convoful data is removed.</p>`;

  root.innerHTML = `
    <div class="trust-shell">
      ${header("support")}
      <main id="trust-main" tabindex="-1">
        <section class="trust-hero trust-hero-support" aria-labelledby="trust-title">
          <p class="eyebrow">Convoful support</p>
          <h1 id="trust-title">Help without sharing the private part.</h1>
          <p class="trust-lede">Tell us what the app did, not the private card itself. A version number, device details, and a careful sequence are usually more useful than a screenshot full of personal content.</p>
          <div class="trust-hero-actions">
            <a class="trust-primary-action" href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20support">Email support <span aria-hidden="true">→</span></a>
            <a href="${WEB_ROUTES.appStoreRelease}">Check release status</a>
          </div>
          <aside class="trust-release-note" aria-label="Release status">
            <strong>Convoful is still pre-release.</strong>
            <p>The first public App Store version has not been submitted. Internal availability or installation does not mean a build has passed its physical release gates.</p>
            <p>Current iOS builds show the legacy name <strong>Agenda!</strong>, and the existing Apple app record is <strong>Agenda! Conversation Cards</strong>. They are the same app and service covered here as Convoful.</p>
          </aside>
        </section>

        <section class="trust-support-grid" aria-labelledby="start-title">
          <div class="trust-section-heading">
            <p class="eyebrow">Start here</p>
            <h2 id="start-title">Send a useful, privacy-safe report.</h2>
          </div>
          <article>
            <span aria-hidden="true">01</span>
            <h3>Version and device</h3>
            <p>Include the version and build shown in About, your iOS version, and the device model. Current iOS builds label the app <strong>Agenda!</strong>, while this site uses Convoful.</p>
          </article>
          <article>
            <span aria-hidden="true">02</span>
            <h3>One exact sequence</h3>
            <p>List what you did, what you expected, what happened, and whether it repeats after returning to a stable screen.</p>
          </article>
          <article>
            <span aria-hidden="true">03</span>
            <h3>Redact first</h3>
            <p>Crop notifications, email addresses, card text, URLs, photos, and audience names. Do not send credentials or magic links.</p>
          </article>
        </section>

        <section class="trust-faq" aria-labelledby="faq-title">
          <div class="trust-section-heading">
            <p class="eyebrow">Quick answers</p>
            <h2 id="faq-title">Before you change or delete anything.</h2>
          </div>
          <details>
            <summary>I did not receive or cannot use my magic link.</summary>
            <p>Request one fresh link and open it on the device where you want to sign in. Do not forward the link to support: it is a credential. An expired or already-used link should return you to one stable recovery path.</p>
          </details>
          <details>
            <summary>A saved Share extension item is waiting.</summary>
            <p>Open Convoful while signed in and connected. Do not clear app data, delete the app, or repeatedly reshare the private item. If the notice returns, send the version/build and the exact visible status without including the saved content.</p>
          </details>
          <details>
            <summary>Can I use Conversation Cards offline?</summary>
            <p>The app keeps an offline working copy for core play. If access was revoked or an account changed, the app may need to reconcile authority before it can safely sync. Avoid reinstalling while work is pending.</p>
          </details>
          <details>
            <summary>What does another Library recipient see?</summary>
            <p>Only the deliberately published recipient-safe Library item and that recipient's progress. The owner's private Notes, Summary, keywords, and Conversation Cards are excluded.</p>
          </details>
          <details>
            <summary>How do I delete my account?</summary>
            ${accountDeletionSupportCopy}
          </details>
          <details>
            <summary>Where are my card-back photos?</summary>
            <p>They stay in app-private storage on the device and are never uploaded. They are decorative and separate from card prompts and attached source media.</p>
          </details>
        </section>

        <section class="trust-safety" aria-labelledby="safety-title">
          <div>
            <p class="eyebrow">Please never email</p>
            <h2 id="safety-title">Passwords, tokens, cookies, or private keys.</h2>
          </div>
          <p>Convoful support does not need your Apple password, magic-link URL, bearer credential, browser cookie, signing key, or environment file. A legitimate support reply will not ask for them.</p>
        </section>

        <section class="trust-contact-card trust-support-contact" aria-labelledby="contact-title">
          <p class="eyebrow">Contact</p>
          <h2 id="contact-title">Email the current support contact.</h2>
          <p>Email <a href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20support">${SUPPORT_EMAIL}</a> for help.</p>
          <div class="trust-contact-links">
            <a class="trust-primary-action" href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20support">Compose an email <span aria-hidden="true">→</span></a>
            <a href="${WEB_ROUTES.privacy}">Read the privacy policy</a>
            <a href="${WEB_ROUTES.howBuilt}">See how Convoful works</a>
          </div>
        </section>
        ${footer()}
      </main>
    </div>`;

  requestAnimationFrame(() => document.querySelector<HTMLElement>("#trust-main")?.focus());
}

export function renderTerms(root: HTMLElement): void {
  preparePage(
    "privacy",
    "Terms of Use",
    "Simple terms for Convoful, a nascent pre-release app whose features may change.",
    TERMS_ROUTE,
  );

  root.innerHTML = `
    <div class="trust-shell" style="overflow-wrap: anywhere">
      ${header("terms")}
      <main id="trust-main" tabindex="-1">
        <section class="trust-hero trust-hero-privacy" aria-labelledby="trust-title">
          <p class="eyebrow">Terms of use · effective ${TERMS_EFFECTIVE_DATE}</p>
          <h1 id="trust-title">Simple terms for a nascent app.</h1>
          <p class="trust-lede">
            Convoful is new and pre-release. Some features are experimental,
            incomplete, unavailable, or may change. These terms set a basic
            ground rule without promising a feature list or launch plan.
          </p>
          <aside class="trust-release-note" aria-label="Release status">
            <strong>Early software, plainly stated.</strong>
            <p>Convoful may add, change, pause, or remove features while the product develops. Nothing on this page commits Convoful to a roadmap, release date, price, or paid feature.</p>
            <a href="${WEB_ROUTES.appStoreRelease}">See the public release burndown <span aria-hidden="true">→</span></a>
          </aside>
        </section>

        <div class="trust-policy-layout">
          <nav class="trust-policy-nav" aria-label="Terms sections">
            <strong>On this page</strong>
            <a href="#use">Use it responsibly</a>
            <a href="#content">Your content</a>
            <a href="#early-service">Early service</a>
            <a href="#app-license">App license</a>
            <a href="#privacy-contact">Privacy & contact</a>
          </nav>

          <div class="trust-policy-copy">
            <section id="use" class="trust-copy-section">
              <p class="eyebrow">01 · Use it responsibly</p>
              <h2>Do not use Convoful to harm people or systems.</h2>
              <p>Do not use Convoful for unlawful, deceptive, harassing, or infringing activity. Do not probe another person's account, interfere with the service, bypass access controls, distribute malware, or use the app in a way that harms another person.</p>
            </section>

            <section id="content" class="trust-copy-section">
              <p class="eyebrow">02 · Your content</p>
              <h2>Your content remains yours.</h2>
              <p>You must have the right to save or share anything you put into Convoful. You give Convoful only the limited permission needed to host, process, copy, and display that content to operate, secure, and maintain the features you choose to use.</p>
              <p>The <a href="${WEB_ROUTES.privacy}">Privacy Policy</a> explains the current boundaries around private content, deliberate sharing, storage, support mail, and account deletion.</p>
            </section>

            <section id="early-service" class="trust-copy-section">
              <p class="eyebrow">03 · Early service</p>
              <h2>Convoful is provided as available.</h2>
              <p>The app may be interrupted, contain errors, lose data, or change without notice while it develops. Keep an independent copy of anything you cannot afford to lose. Convoful may suspend or end access when needed to protect people, secure the service, comply with law, or stop abuse.</p>
              <p>Nothing here promises a paid feature or launch date. If Convoful later offers something for purchase, the price and billing terms will be shown before you choose to buy it.</p>
            </section>

            <section id="app-license" class="trust-copy-section">
              <p class="eyebrow">04 · App license</p>
              <h2>Apple's terms still apply to the iOS app.</h2>
              <p>If you receive Convoful through Apple, <a class="trust-inline-cta" href="${APPLE_STANDARD_EULA_URL}" target="_blank" rel="noreferrer">Apple's Standard EULA <span aria-hidden="true">↗</span></a> governs the app license. These Convoful Terms govern the hosted service and do not replace Apple's agreement.</p>
            </section>

            <section id="privacy-contact" class="trust-copy-section trust-contact-card">
              <p class="eyebrow">05 · Privacy, changes, and contact</p>
              <h2>Read the current policy, not a feature wish list.</h2>
              <p>The <a href="${WEB_ROUTES.privacy}">Privacy Policy</a> describes current data practices. Material changes to these terms update the effective date on this page and receive notice when applicable law requires it.</p>
              <p>Questions can be sent to <a href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20terms">${SUPPORT_EMAIL}</a>.</p>
              <div class="trust-contact-links">
                <a class="trust-primary-action" href="mailto:${SUPPORT_EMAIL}?subject=Convoful%20terms">Email about the terms <span aria-hidden="true">→</span></a>
                <a href="${WEB_ROUTES.privacy}">Read the Privacy Policy</a>
              </div>
            </section>
          </div>
        </div>
        ${footer()}
      </main>
    </div>`;

  requestAnimationFrame(() => document.querySelector<HTMLElement>("#trust-main")?.focus());
}
